import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { ApiService } from '@app/core/services/api/api.service';
import { CarriersService } from '@app/pages/delivery-service/services';
import { CarriersActions } from '@app/pages/delivery-service/store/actions';
import * as fromCarriers from '@app/pages/delivery-service/store/reducers';
import { Carrier, CarrierTypes } from '@app/services/carriers/carrier.model';
import { ShopService } from '@app/services/shops/shop.service';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { TimeUnits } from '@utils/times-utils';
import { first } from 'rxjs/operators';
import { CarrierFormComponent } from '../carrier-form/carrier-form.component';
import {
  InternalInegrationNameToTrackstarNameMap,
  TrackstarConnectButtonComponent,
  TrackstarIntegrationName,
} from '../trackstar-component/trackstar-component.component';

const carrierPropertyMap: { [key: string]: { [field: string]: string } } = {
  [TrackstarIntegrationName.Extensiv_3PL_Central.toLowerCase()]: {
    client_id: 'customerProp1',
    client_secret: 'customerPassword',
    user_login_id: 'customerKey',
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.NFI_3PL]]: {
    sender_id: 'customerKey',
    access_token: 'customerPassword',
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.Amazon_Mcf_3PL]]: {},
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.STACI_AMERICA]]: {
    client_id: 'customerProp1',
    user_login_id: 'customerKey',
  },
  [InternalInegrationNameToTrackstarNameMap[TrackstarIntegrationName.CAPACITY]]: {
    client_id: 'customerProp1',
    client_secret: 'customerProp3',
    client_key: 'customerKey',
    client_code: 'customerProp4',
  },
  // Add other integrations and their mappings here

  // [TrackstarIntegrationName.Another_integration]: {
  //   bearerToken: 'customerToken',
  //   apiKey: 'customerApiKey'
  // },
};
@Component({
  selector: 'app-create-carrier',
  templateUrl: './create-carrier.component.html',
  styleUrls: ['./create-carrier.component.scss'],
  standalone: false,
})
export class CreateCarrierComponent implements OnInit, OnDestroy {
  @ViewChild(CarrierFormComponent) carrierForm: CarrierFormComponent;
  @Input() carrierType: CarrierTypes;
  @Input() shopID: number;
  @Input() drawer: any;
  @Output() onClickBack = new EventEmitter();
  @Output() closeAfterAction = new EventEmitter();
  @Output() isPending = new EventEmitter<boolean>();
  @Output() addNewCarrierEmit = new EventEmitter<Carrier>();

  formFieldsLoaded: boolean = false;
  carrierFieldsConfig: any;
  destroyed$ = new Subject<boolean>();
  pending$ = this.store.select(fromCarriers.selectPendingAddCarrier);
  errorMessage$ = this.store.select(fromCarriers.selectErrorMessageAddCarrier);
  ianaTimeZone: string = 'Asia/Jerusalem';
  isOnTrialPlan: boolean = false;
  trackStarConfig: any;
  trackStarCarrier: Carrier;
  @ViewChild('trackStarComponent', { read: ViewContainerRef, static: true }) vcRef: ViewContainerRef;

  constructor(
    private store: Store<fromCarriers.State>,
    private carriersService: CarriersService,
    private shopService: ShopService,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly apiService: ApiService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadEditableCarrierFields(this.carrierType);
    this.initShopService();
  }

  initShopService() {
    this.shopService.shop$.pipe(first()).subscribe((currentShop) => {
      this.isOnTrialPlan = currentShop?.isOnTrialPlan;
      this.ianaTimeZone = currentShop?.metadata?.ianaTimeZone;
    });
  }

  initTrackStarConfig() {
    this.trackStarConfig = {
      onSuccess: async (authCode: string) => {
        await fetch(`${this.apiService.API_URL}trackstar/store-token`, {
          method: 'POST',
          body: JSON.stringify({
            authCode: authCode,
            shopId: this.apiService.shopId,
          }),
        });
      },
      onClose: (data) => {
        if (data?.errorMsg1 && data?.errorMsg2) {
          this.showToast(data?.errorMsg1, 'danger', 5);
          this.showToast(data?.errorMsg2, 'danger', 5);
        } else if (data?.successMsg) {
          this.showToast(data.successMsg, 'success', 5);
          const carrier = this.trackStarCarrier;
          this.store.dispatch(CarriersActions.addNewCarrier({ carrier, shopID: this.shopID }));
        }
        this.isPending.emit(false);
      },
      onLoad: () => {
        console.log('loaded');
      },
      getLinkToken: async () => {
        const res = await fetch(`${this.apiService.API_URL}trackstar/get-link-token`, {
          method: 'POST',
        });
        if (!res.ok) {
          console.error('Failed to get link token');
          return;
        }
        const { linkToken } = await res.json();
        return linkToken;
      },
      sandbox: true,
    };
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onSubmit(): void {
    this.carrierForm.onSubmit();
  }

  submitForm(values: { carrier: Carrier; metadata?: { reauthenticate?: boolean } }): void {
    this.addNewCarrier(values.carrier);
  }

  private loadEditableCarrierFields(carrierType: CarrierTypes) {
    this.carriersService.getCarrierFieldsConfig(carrierType.name).subscribe((data) => {
      this.formFieldsLoaded = true;
      this.carrierFieldsConfig = data;
      this.changeDetectorRef.detectChanges();
    });
  }

  private addNewCarrier(carrier: Carrier): void {
    this.isPending.emit(true);
    if (carrier.type.custom_fields?.trackStarIntegration) {
      this.addNewTrackStarCarrier(carrier);
    } else {
      this.store.dispatch(CarriersActions.addNewCarrier({ carrier, shopID: this.shopID }));
    }
  }

  private addNewTrackStarCarrier(carrier: Carrier): void {
    const integrationFields = carrierPropertyMap[InternalInegrationNameToTrackstarNameMap[carrier.type.name]];
    if (!integrationFields) {
      console.error('Integration fields not found for carrier type', carrier.type.name);
      this.isPending.emit(false);
      return;
    }
    this.trackStarCarrier = carrier;
    this.initTrackStarConfig();
    const componentRef = this.vcRef.createComponent(TrackstarConnectButtonComponent);
    const credentialsConfiguration: { [key: string]: string } = {};
    Object.keys(integrationFields).forEach((field) => {
      credentialsConfiguration[field] = carrier[integrationFields[field]];
    });
    componentRef.instance.config = this.trackStarConfig;
    componentRef.instance.integrationName = InternalInegrationNameToTrackstarNameMap[carrier.type.name];
    componentRef.instance.credentialsConfiguration = credentialsConfiguration;
    componentRef.instance.openDialogAfterInit = true;
    componentRef.instance.doNotHideModal = carrier.type.custom_fields.trackStarIntegration.doNotHideModal; // you can use it for tracking issues with the modal
    componentRef.instance.isSandBox = carrier.isTestMode;
  }
  private showToast(message, mode: 'danger' | 'success' | 'info' | 'secondary', duration) {
    this.toastService.show(PdqToastComponent, {
      mode: mode,
      message: message,
      duration: TimeUnits._SECOND_IN_MS * duration,
    });
  }
}
